import { FlightDetails, I18NCity, Station } from "../interface/flight.details";
import { getRemarkKey, RemarkToCodeMapping, TripType } from "@industronics/fids-utils";
import { IATACityI18N } from "../lib/iata.city.i18";
import { airlinesDestinations } from "../lib/airlines.destinations";
import { RemarkTranslations } from "../lib/remarks.translation";
import { IATACityLanguageMap } from "../lib/city.lang.map";
import { DeviceConfigService } from "../services/deviceConfigService";
import { POS_AVIATION_EXCLUDED_REMARK_CODES, POS_AVIATION_REMARK_LANGS, POS_AVIATION_STATION_LANGS } from "./constants";

// True when this remark should be blanked out because the device was registered
// with POS-AVIATION enabled and the key is listed in POS_AVIATION_EXCLUDED_REMARK_CODES.
// The flight row still displays — only its remark text is suppressed.
export const isPosAviationHiddenRemark = (remarkKey?: RemarkToCodeMapping | string): boolean => {
    if (!remarkKey) return false;
    if (!POS_AVIATION_EXCLUDED_REMARK_CODES.length) return false;
    if (!DeviceConfigService.isPosAviation()) return false;

    return (POS_AVIATION_EXCLUDED_REMARK_CODES as string[]).includes(remarkKey);
};

// Keeps only the allowed languages in a translation list when the device was
// registered with POS-AVIATION enabled. Entries carrying no `lang` are always
// kept. No-op when the flag is off.
const filterByPosAviationLangs = (translations: I18NCity[] | undefined, langs: string[]): I18NCity[] => {
    if (!translations?.length) return [];
    if (!DeviceConfigService.isPosAviation()) return translations;
    return translations.filter((item) => !item?.lang || langs.includes(item.lang));
};

// Remark translations: English + Malay only under POS-AVIATION.
export const filterPosAviationLangs = (translations?: I18NCity[]): I18NCity[] =>
    filterByPosAviationLangs(translations, POS_AVIATION_REMARK_LANGS);

// Station (destination) translations: English + Malay only under POS-AVIATION.
export const filterPosAviationStationLangs = (translations?: I18NCity[]): I18NCity[] =>
    filterByPosAviationLangs(translations, POS_AVIATION_STATION_LANGS);

export const getDisplayColor = (remarkCodeKey: string) => {
    let result = "";
    switch (remarkCodeKey) {
        case RemarkToCodeMapping.DELAYED:
        case RemarkToCodeMapping.CANCELLED:
        case RemarkToCodeMapping.GATE_CLOSED:
            result = "text-red";
            break;
        case RemarkToCodeMapping.FINAL_CALL:
        case RemarkToCodeMapping.LAST_BAG:
            result = "text-warning";
            break;

        case RemarkToCodeMapping.DEPARTED:
        case RemarkToCodeMapping.LANDED:
            result = "text-done";
            break;
        case RemarkToCodeMapping.GATE_OPEN:
        case RemarkToCodeMapping.OPEN:
        case RemarkToCodeMapping.CAROUSEL_OPEN:
            result = "text-pre";
            break;
        default:
            result = "text-defualt";
            break;
    };
    return result;
};

export const normalizeCode = (code: string) => {
    if (!code) return;
    return code.replace(/([A-Za-z]+)0*(\d+)/, (_, prefix, number) => `${prefix}${parseInt(number, 10)}`);
}

export const toCamelCase = (prop: string): string => {
    if (prop) return prop.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
    return "";
};


export const isSubset = (parentArray: any[], subsetArray: any[]) => {
    // Create a frequency map for the parent array
    const parentFrequency: any = {};
    for (const element of parentArray) {
        parentFrequency[element] = (parentFrequency[element] || 0) + 1;
    }

    // Check each element in the subset array
    for (const element of subsetArray) {
        // If the element is not in the parent array or its count exceeds, return false
        if (!parentFrequency[element] || --parentFrequency[element] < 0) {
            return false;
        }
    }

    return true;
}


export const applyBodyStyles = (styleArray: any) => {
    styleArray.forEach((style: any) => {
        document.body.style[style.prop] = style.value;
    });
};

export const isVideoFile = (filename: string) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.mpeg', '.3gp'];
    const ext = filename.toLowerCase().split('.').pop();
    return ext ? videoExtensions.includes(`.${ext}`) : false;
}

export interface FinalStation {
    city: string;
    last: boolean;
}

export const getTranslatedCities = (flight: FlightDetails): FinalStation[] => {

    const stations: FinalStation[] = [];
    if (flight.tripType === TripType.Departure) {
        const updatedStations = flight.stations.filter((_: any, index: number) => index !== 0);

        updatedStations.forEach((item, index) => {
            const city = item.city;
            if (city) stations.push({ city: city, last: index + 1 === updatedStations.length && updatedStations.length > 1 });
            const i18City = IATACityI18N[item.airport];
            if (i18City && city !== i18City) stations.push({ city: i18City, last: index + 1 === updatedStations.length && updatedStations.length > 1 });
            const airineCode: string = flight.flightNumber.split(" ")[0];
            let translationByOrigin;
            if (airlinesDestinations[airineCode]) translationByOrigin = airlinesDestinations[airineCode][item.airport];
            if (translationByOrigin && city !== translationByOrigin && i18City !== translationByOrigin) stations.push({ city: translationByOrigin, last: index + 1 === updatedStations.length && updatedStations.length > 1 });
        });
    }
    else {
        const updatedStations = flight.stations.filter((_: any, index: number) => index !== flight.stations.length - 1);

        updatedStations.forEach((item, index) => {
            const city = item.city;
            stations.push({ city: city, last: index === 0 && updatedStations.length > 1 });
            const i18City = IATACityI18N[item.airport];
            if (i18City && city !== i18City) stations.push({ city: i18City, last: index === 0 && updatedStations.length > 1 });
            const airineCode: string = flight.flightNumber.split(" ")[0];
            const translationByOrigin = airlinesDestinations[airineCode][item.airport];
            console.log('hello', translationByOrigin);
            if (translationByOrigin && city !== translationByOrigin && i18City !== translationByOrigin) stations.push({ city: translationByOrigin, last: index === 0 && updatedStations.length > 1 });
        });
    }
    return stations;
};

export const getRemarksTranslation = (remarkKey: RemarkToCodeMapping, stations: Station[]): string[] => {

    // const remarkKey = RemarkToCodeMapping.GO_TO_GATE;
    if (!remarkKey) return [];
    const translatedRemark = RemarkTranslations["ml"][remarkKey];
    const remarksList: any = [getRemarkKey(remarkKey)];
    remarksList.push(translatedRemark);

    const zhStations = stations.filter(
        (station: Station) => IATACityLanguageMap[station.airport] === "zh"
    );
    if (zhStations.length) {
        const translatedRemark = RemarkTranslations["zh"][remarkKey];
        remarksList.push(translatedRemark);
    }

    return remarksList;

};